/**
 * Pure helper — given the Table Explorer tab content and the optional FK
 * Breadcrumb navigation tree, decide the effective rendering context.
 *
 * The result drives whether the workspace shows a TABLE grid (with locked
 * inbound-FK filters and any user-added filters) or a ROW single-row view.
 *
 * Concerns 4 + 6 of the FK breadcrumb follow-up: clicking a non-root
 * breadcrumb chip must switch the grid context to that node, and the
 * inbound-FK filter inherited from the parent row must render as a locked
 * chip in the FilterWizard — not silently applied behind the user's back.
 */

import type {
  FilterGroup,
  NavigationNode,
  NavigationTree,
  SortDefinition,
} from '../../../core/types/workspaceTypes';

export type EffectiveTableContext = {
  readonly kind: 'table';
  readonly schema: string;
  readonly table: string;
  /** System-applied filters; rendered as locked (read-only) chips. */
  readonly lockedFilters: ReadonlyArray<FilterGroup>;
  /** User-added filters on top of the locked ones; fully editable. */
  readonly userFilters: ReadonlyArray<FilterGroup>;
  /** Sorting applied to the current view (always user-controlled). */
  readonly sorting: ReadonlyArray<SortDefinition>;
  /**
   * The TableNode driving this context, or null when there's no nav tree
   * (i.e. the tab is rendering its root table directly).
   */
  readonly node: NavigationNode | null;
};

export type EffectiveRowContext = {
  readonly kind: 'row';
  readonly node: NavigationNode;
};

export type EffectiveExplorerContext = EffectiveTableContext | EffectiveRowContext;

type TabContentSlice = {
  readonly schema: string;
  readonly tableName: string;
  readonly filters: ReadonlyArray<FilterGroup>;
  readonly sorting: ReadonlyArray<SortDefinition>;
};

export function computeEffectiveExplorerContext(
  tabContent: TabContentSlice,
  tree: NavigationTree | null,
): EffectiveExplorerContext {
  if (!tree) {
    return {
      kind: 'table',
      schema: tabContent.schema,
      table: tabContent.tableName,
      lockedFilters: [],
      userFilters: tabContent.filters,
      sorting: tabContent.sorting,
      node: null,
    };
  }

  const active = tree.nodes[tree.activeNodeId];
  if (!active) {
    return {
      kind: 'table',
      schema: tabContent.schema,
      table: tabContent.tableName,
      lockedFilters: [],
      userFilters: tabContent.filters,
      sorting: tabContent.sorting,
      node: null,
    };
  }

  if (active.kind === 'row') {
    return { kind: 'row', node: active };
  }

  // Root TableNode → tab content is the source of truth (user opened this
  // table directly; their filters/sorting live on tabContent).
  if (active.id === tree.rootId) {
    return {
      kind: 'table',
      schema: active.schema,
      table: active.table,
      lockedFilters: [],
      userFilters: tabContent.filters,
      sorting: tabContent.sorting,
      node: active,
    };
  }

  // Non-root TableNode → node IS the source of truth. Its `filters` are the
  // locked inbound-FK constraints; user additions live in `userFilters`.
  return {
    kind: 'table',
    schema: active.schema,
    table: active.table,
    lockedFilters: active.filters,
    userFilters: active.userFilters ?? [],
    sorting: active.userSorting ?? [],
    node: active,
  };
}
